import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { useMemo, type FC } from "react";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import { getSportOptions } from "../../../../queries/game-pass/gamePassOptions";
import { useGetTaskOptions } from "../../../../queries/game-pass/useGetTaskOptions";
import { Input, Select } from "../../../shared";
import {
  MONTHLY_ACTION_OPTIONS,
  MONTHLY_PICK_TYPE_OPTIONS,
  sanitizeDecimalInput,
  sanitizeSignedIntegerInput,
  type BetCurrency,
  type MonthlyPickType,
  type MonthlyPlayWinAction,
} from "../monthlyChallengeData";

import styles from "./MonthlySportsbookPicksFields.module.css";

/** Partial draft update the card merges into the challenge — scoped to the
 *  sportsbook fields so a Sports change can atomically prune now-orphaned
 *  Leagues in a single re-render. */
export type MonthlySportsbookPicksPatch = {
  pickType?: MonthlyPickType;
  minimumOdds?: string;
  sportsbookAction?: MonthlyPlayWinAction;
  sports?: ReadonlyArray<string>;
  leagues?: ReadonlyArray<string>;
  sportsbookMinBet?: string;
  wagerCurrency?: BetCurrency;
};

export type MonthlySportsbookPicksFieldsProps = {
  pickType: MonthlyPickType;
  minimumOdds: string;
  action: MonthlyPlayWinAction;
  sports: ReadonlyArray<string>;
  leagues: ReadonlyArray<string>;
  /** Minimum wager a pick must meet to count; free-form string ("" = unset),
   *  sanitised to a money-style decimal on change. */
  minBet: string;
  /** Currency a qualifying wager must be placed in (required). `undefined`
   *  until the admin picks one. */
  wagerCurrency: BetCurrency | undefined;
  /** Currency options sourced from `GET /config` (same catalogue as the Season
   *  task form); empty while the config is loading. */
  wagerCurrencyOptions: ReadonlyArray<{ value: BetCurrency; label: string }>;
  onChange: (patch: MonthlySportsbookPicksPatch) => void;
  /** Read-only mode (locked / Active view): disables every control so the
   *  sportsbook picks render inspect-only. */
  disabled?: boolean;
  idPrefix: string;
};

type LabeledOption = { value: string; label: string };

/**
 * Leagues from `leagues` that still belong to at least one sport in `sports`,
 * given the sport catalogue. Used to drop orphaned leagues the moment a parent
 * sport is deselected, so a league can never persist without its sport.
 */
export const pruneLeaguesForSports = (
  sports: ReadonlyArray<string>,
  leagues: ReadonlyArray<string>,
  catalogue: ReadonlyArray<{
    value: string;
    leagues: ReadonlyArray<{ value: string }>;
  }>,
): string[] => {
  const allowedLeagues = new Set<string>();
  for (const sport of catalogue) {
    if (!sports.includes(sport.value)) continue;
    for (const league of sport.leagues) allowedLeagues.add(league.value);
  }
  return leagues.filter((id) => allowedLeagues.has(id));
};

/**
 * The "Sportsbook Picks" task-type field set (FE-2277): a Pick Type dropdown, a
 * Play / Win Action toggle, Sports and (sport-scoped) Leagues multi-selects, a
 * Minimum Odds floor and a Minimum Bet Amount. The sports/leagues catalogue is
 * read from `GET /task-options` (the same source Season uses) with a bundled
 * static fallback, so the selects always have options even while the request is
 * loading or errored.
 *
 * Returns the cells as a fragment so the card can lay them out with the Task
 * Type dropdown in one shared 2-column grid, auto-flowing column-by-column in
 * DOM order:
 *   `Task Type | Pick Type`
 *   `Min Odds  | Action`
 *   `Sports    | Leagues`
 *   `Min Bet   | Wager Currency`
 * (every cell filled — no spacers needed).
 */
const MonthlySportsbookPicksFields: FC<MonthlySportsbookPicksFieldsProps> = ({
  pickType,
  minimumOdds,
  action,
  sports,
  leagues,
  minBet,
  wagerCurrency,
  wagerCurrencyOptions,
  onChange,
  disabled = false,
  idPrefix,
}) => {
  const taskOptionsQuery = useGetTaskOptions();

  // Full sport catalogue (API-driven with a static fallback baked into
  // `getSportOptions`), so options never empty out mid-load.
  const sportCatalogue = useMemo(
    () => getSportOptions(taskOptionsQuery.data),
    [taskOptionsQuery.data],
  );

  const pickTypeOptions = useMemo<ReadonlyArray<LabeledOption>>(
    () =>
      MONTHLY_PICK_TYPE_OPTIONS.map((option) => ({
        value: option.value,
        label: AppStrings[option.labelKey],
      })),
    [],
  );

  const sportOptions = useMemo<ReadonlyArray<LabeledOption>>(
    () =>
      sportCatalogue.map((sport) => ({
        value: sport.value,
        label: sport.label,
      })),
    [sportCatalogue],
  );

  // Leagues are scoped to the currently-selected sports (deduped by id), so the
  // dropdown only ever offers leagues that belong to a chosen sport.
  const leagueOptions = useMemo<ReadonlyArray<LabeledOption>>(() => {
    const byValue = new Map<string, string>();
    for (const sport of sportCatalogue) {
      if (!sports.includes(sport.value)) continue;
      for (const league of sport.leagues) {
        byValue.set(league.value, league.label);
      }
    }
    return Array.from(byValue, ([value, label]) => ({ value, label }));
  }, [sportCatalogue, sports]);

  const hasSports = sports.length > 0;

  const handleSportsChange = (values: string[]): void => {
    // Prune any selected league that no longer belongs to a still-selected
    // sport, so switching sports can never leave an orphaned league on the
    // draft.
    onChange({
      sports: values,
      leagues: pruneLeaguesForSports(values, leagues, sportCatalogue),
    });
  };

  return (
    <>
      <label className={styles.field}>
        <span className={styles.fieldLabel}>
          {AppStrings.MONTHLY_CHALLENGE_PICK_TYPE_LABEL}
        </span>
        <Select<MonthlyPickType>
          className={styles.select}
          value={pickType}
          options={[...pickTypeOptions]}
          aria-label={AppStrings.MONTHLY_CHALLENGE_PICK_TYPE_LABEL}
          disabled={disabled}
          onChange={(value) => onChange({ pickType: value })}
          data-testid={`${idPrefix}-pick-type`}
        />
      </label>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>
          {AppStrings.MONTHLY_CHALLENGE_MIN_ODDS_LABEL}
          <Tooltip title={AppStrings.MONTHLY_CHALLENGE_MIN_ODDS_INFO}>
            <InfoCircleOutlined
              className={styles.infoIcon}
              aria-label={AppStrings.MONTHLY_CHALLENGE_MIN_ODDS_INFO}
            />
          </Tooltip>
        </span>
        <Input
          className={styles.input}
          value={minimumOdds}
          placeholder={AppStrings.MONTHLY_CHALLENGE_MIN_ODDS_PLACEHOLDER}
          aria-label={AppStrings.MONTHLY_CHALLENGE_MIN_ODDS_LABEL}
          disabled={disabled}
          onChange={(e) =>
            onChange({ minimumOdds: sanitizeSignedIntegerInput(e.target.value) })
          }
          data-testid={`${idPrefix}-min-odds`}
        />
      </label>

      <div className={styles.field}>
        <span className={styles.fieldLabel}>
          {AppStrings.MONTHLY_CHALLENGE_ACTION_LABEL}
        </span>
        <div
          className={styles.pills}
          role="radiogroup"
          aria-label={AppStrings.MONTHLY_CHALLENGE_ACTION_LABEL}
          data-testid={`${idPrefix}-action`}
        >
          {MONTHLY_ACTION_OPTIONS.map((option) => {
            const isActive = option.value === action;
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={isActive}
                disabled={disabled}
                className={`${styles.pill} ${isActive ? styles.pillActive : ""}`.trim()}
                onClick={() => onChange({ sportsbookAction: option.value })}
                data-testid={`${idPrefix}-action-${option.value.toLowerCase()}`}
              >
                {AppStrings[option.labelKey]}
              </button>
            );
          })}
        </div>
      </div>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>
          {AppStrings.MONTHLY_CHALLENGE_SPORTS_LABEL}
        </span>
        <Select<string[]>
          className={styles.select}
          mode="multiple"
          value={[...sports]}
          options={[...sportOptions]}
          placeholder={AppStrings.MONTHLY_CHALLENGE_SPORTS_PLACEHOLDER}
          aria-label={AppStrings.MONTHLY_CHALLENGE_SPORTS_LABEL}
          disabled={disabled}
          showSearch
          optionFilterProp="label"
          loading={taskOptionsQuery.isLoading}
          onChange={handleSportsChange}
          data-testid={`${idPrefix}-sports`}
        />
      </label>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>
          {AppStrings.MONTHLY_CHALLENGE_LEAGUES_LABEL}
        </span>
        <Select<string[]>
          className={styles.select}
          mode="multiple"
          value={[...leagues]}
          options={[...leagueOptions]}
          placeholder={
            hasSports
              ? AppStrings.MONTHLY_CHALLENGE_LEAGUES_PLACEHOLDER
              : AppStrings.MONTHLY_CHALLENGE_LEAGUES_SELECT_SPORT_FIRST
          }
          aria-label={AppStrings.MONTHLY_CHALLENGE_LEAGUES_LABEL}
          disabled={disabled || !hasSports}
          showSearch
          optionFilterProp="label"
          onChange={(values) => onChange({ leagues: values })}
          data-testid={`${idPrefix}-leagues`}
        />
      </label>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>
          {AppStrings.MONTHLY_CHALLENGE_MIN_BET_LABEL}
          <Tooltip title={AppStrings.MONTHLY_CHALLENGE_MIN_BET_INFO}>
            <InfoCircleOutlined
              className={styles.infoIcon}
              aria-label={AppStrings.MONTHLY_CHALLENGE_MIN_BET_INFO}
            />
          </Tooltip>
        </span>
        <Input
          className={styles.input}
          value={minBet}
          placeholder={AppStrings.MONTHLY_CHALLENGE_MIN_BET_PLACEHOLDER}
          aria-label={AppStrings.MONTHLY_CHALLENGE_MIN_BET_LABEL}
          disabled={disabled}
          onChange={(e) =>
            onChange({ sportsbookMinBet: sanitizeDecimalInput(e.target.value) })
          }
          data-testid={`${idPrefix}-min-bet`}
        />
      </label>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>
          {AppStrings.MONTHLY_CHALLENGE_WAGER_CURRENCY_LABEL}
          <Tooltip title={AppStrings.MONTHLY_CHALLENGE_WAGER_CURRENCY_INFO}>
            <InfoCircleOutlined
              className={styles.infoIcon}
              aria-label={AppStrings.MONTHLY_CHALLENGE_WAGER_CURRENCY_INFO}
            />
          </Tooltip>
        </span>
        <Select<BetCurrency>
          className={styles.select}
          value={wagerCurrency}
          options={[...wagerCurrencyOptions]}
          placeholder={AppStrings.MONTHLY_CHALLENGE_WAGER_CURRENCY_PLACEHOLDER}
          aria-label={AppStrings.MONTHLY_CHALLENGE_WAGER_CURRENCY_LABEL}
          disabled={disabled}
          onChange={(value) => onChange({ wagerCurrency: value })}
          data-testid={`${idPrefix}-wager-currency`}
        />
      </label>

    </>
  );
};

export default MonthlySportsbookPicksFields;
